import { NavLink } from 'react-router-dom'
import { Search, Loader2 } from 'lucide-react'
import PortfolioSelector from '../PortfolioSelector'
import NotificationsDropdown from '../NotificationsDropdown'
import SearchResultsDropdown from '../search/SearchResultsDropdown'
import type { SearchResult } from '../../types/navigation'

interface DesktopHeaderProps {
  firstName: string
  isAgent: boolean
  isTeamMember?: boolean
  agencyName?: string | null
  searchQuery: string
  searchResults: SearchResult[]
  showSearchResults: boolean
  searchLoading: boolean
  searchRef: React.RefObject<HTMLDivElement | null>
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onResultClick: (result: SearchResult) => void
  onSearchFocus: () => void
}

export default function DesktopHeader({
  firstName,
  isAgent,
  isTeamMember,
  agencyName,
  searchQuery,
  searchResults,
  showSearchResults,
  searchLoading,
  searchRef,
  onSearchChange,
  onResultClick,
  onSearchFocus,
}: DesktopHeaderProps) {
  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  return (
    <header className="hidden lg:block sticky top-0 z-20" style={{
      background: 'var(--bg-card)',
      borderBottom: '1px solid var(--border-color)',
      padding: '20px 32px',
    }}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {isTeamMember
              ? `Managing ${agencyName ? `${agencyName}'s` : "the agency's"} portfolios`
              : isAgent ? "Here's what's happening across your portfolios today" : "Here's what's happening across your properties today"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Portfolio selector (agents only) */}
          <PortfolioSelector />
          {/* Search box */}
          <div ref={searchRef} className="relative">
            <div
              className="flex items-center gap-2 px-3 py-1 rounded-lg w-52"
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-color)',
              }}
            >
              {searchLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} />
              ) : (
                <Search className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              )}
              <input
                type="text"
                value={searchQuery}
                onChange={onSearchChange}
                onFocus={onSearchFocus}
                placeholder="Search..."
                className="text-sm flex-1 placeholder:text-[var(--text-muted)]"
                style={{
                  color: 'var(--text-primary)',
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  WebkitAppearance: 'none',
                }}
              />
            </div>

            {/* Search Results Dropdown */}
            <SearchResultsDropdown
              results={searchResults}
              showResults={showSearchResults}
              searchQuery={searchQuery}
              searchLoading={searchLoading}
              onResultClick={onResultClick}
              variant="desktop"
            />
          </div>
          {/* Notifications */}
          <NotificationsDropdown />
          {/* Logo icon */}
          <NavLink to="/dashboard">
            <svg viewBox="0 0 120 120" className="w-10 h-10 rounded-xl">
              <rect width="120" height="120" rx="26" fill="#1a2744"/>
              <path d="M36 30V86H84" stroke="#ffffff" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <circle cx="84" cy="86" r="10" fill="#c87941"/>
            </svg>
          </NavLink>
        </div>
      </div>
    </header>
  )
}
